import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import SeverityBadge from "@/components/shared/SeverityBadge";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Bell, Check, MapPin, Activity, Clock, Loader2, Send, Flame } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api, { extractApiError } from "@/lib/api";
import { filterUnrespondedByOrg } from "@/lib/incidentScope";
import { downloadReport } from "@/lib/downloadReport";
import { formatDisplayId } from "@/lib/utils";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import LocationLabel from "@/components/shared/LocationLabel";

const SERVICE_RESPONSE_STATUSES = ["DISPATCHED", "EN_ROUTE", "ON_SCENE", "RESOLVED", "UNABLE_TO_RESPOND"];

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeSeverity(value) {
  const s = String(value || "low").toLowerCase();
  return ["critical", "high", "medium", "low"].includes(s) ? s : "low";
}

function formatIncidentType(value) {
  return String(value || "Incident")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatEventTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mapIncident(item) {
  return {
    id: item?.id || item?.incident_id || "",
    status: String(item?.status || "OPEN").toUpperCase(),
    type: formatIncidentType(item?.incident_type || item?.type),
    severity: normalizeSeverity(item?.severity),
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    location:
      item?.location?.address ||
      item?.location ||
      item?.address ||
      item?.place ||
      [item?.city, item?.state].filter(Boolean).join(", ") ||
      "Location unavailable",
    timestamp: item?.event_time || item?.created_at || item?.updated_at || item?.timestamp || "",
    distance_km: item?.distance_km,
  };
}

export default function FireNewAlerts() {
  const { can } = useAuth();
  const canUpdateIncidents = can("INCIDENTS.UPDATE");
  const canDispatchIncidents = can("INCIDENTS.DISPATCH");
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });
  const [incidentTypeFilter, setIncidentTypeFilter] = useState("ALL");
  const [statusSubmittingId, setStatusSubmittingId] = useState(null);

  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(SERVICE_RESPONSE_STATUSES[0]);
  const [serviceNote, setServiceNote] = useState("");
  const [responseSubmitting, setResponseSubmitting] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        status: "OPEN",
        page,
        page_size: pageSize,
      };
      if (incidentTypeFilter === "FIRE") params.incident_type = "FIRE";

      const { data } = await api.get("/incidents", { params });
      const rows = filterUnrespondedByOrg(normalizeCollection(data)).map(mapIncident).filter((x) => x.id);
      setList(rows);
      setMeta(data?.meta || { total: rows.length, page, page_size: pageSize, pages: 1 });
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load fire alerts.");
      setList([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, incidentTypeFilter]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useIncidentSocket((incidentData) => {
    const row = mapIncident(incidentData);
    if (!row.id) return;

    if (incidentTypeFilter === "FIRE") {
      const rawType = String(incidentData?.incident_type || "").toUpperCase();
      if (rawType !== "FIRE") return;
    }

    setList((prev) => {
      const exists = prev.some((item) => item.id === row.id);
      if (row.status !== "OPEN") {
        if (!exists) return prev;
        setMeta((current) => ({ ...current, total: Math.max(0, (current?.total || 0) - 1) }));
        return prev.filter((item) => item.id !== row.id);
      }
      if (exists) return prev.map((item) => (item.id === row.id ? row : item));

      setMeta((current) => ({ ...current, total: (current?.total || 0) + 1 }));
      return [row, ...prev].slice(0, pageSize);
    });
  });

  const acknowledge = async (id) => {
    setStatusSubmittingId(id);
    try {
      await api.post(`/incidents/${id}/service-response`, { status: "ACKNOWLEDGED" });
      toast.success("Incident acknowledged.");
      await fetchAlerts();
    } catch (err) {
      toast.error(extractApiError(err).message || "Failed to acknowledge alert.");
    } finally {
      setStatusSubmittingId(null);
    }
  };

  const openServiceDialog = (incident) => {
    setSelectedIncident(incident);
    setServiceStatus(SERVICE_RESPONSE_STATUSES[0]);
    setServiceNote("");
    setServiceDialogOpen(true);
  };

  const submitServiceResponse = async () => {
    if (!selectedIncident?.id) return;
    setResponseSubmitting(true);
    try {
      const payload = {
        status: serviceStatus,
        ...(serviceNote.trim() ? { note: serviceNote.trim() } : {}),
      };
      await api.post(`/incidents/${selectedIncident.id}/service-response`, payload);
      toast.success(`Service response logged: ${serviceStatus}.`);
      setServiceDialogOpen(false);
      await fetchAlerts();
    } catch (err) {
      toast.error(extractApiError(err).message || "Failed to log service response.");
    } finally {
      setResponseSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fire-new-alerts">
      <PageHeader
        title="New Alerts"
        subtitle="Open incidents routed to your fire department."
        onDownload={() => {
          downloadReport({
            filename: "fire_new_alerts",
            title: "Fire - New Alerts Report",
            headers: ["ID", "Type", "Severity", "Location", "Time"],
            rows: list.map((i) => [i.id, i.type, i.severity, i.location, formatEventTime(i.timestamp)]),
          });
          toast.success("Report downloaded");
        }}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate("/fire/active")}>
              <Flame size={14} className="mr-1" /> Active Incidents
            </Button>
            <Button size="sm" variant="outline" onClick={fetchAlerts} disabled={loading}>
              {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
              Refresh
            </Button>
          </div>
        }
      />

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div className="text-xs text-slate-500">{loading ? "Loading alerts..." : `${meta.total} open incidents`}</div>
        <div className="flex items-center gap-2">
          <Select value={incidentTypeFilter} onValueChange={(value) => { setIncidentTypeFilter(value); setPage(1); }}>
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue placeholder="Incident type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All routed</SelectItem>
              <SelectItem value="FIRE">Fire only</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500">Page {meta.page || page} of {meta.pages || 1}</span>
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState title="No new alerts" description="No open incidents right now." icon={Bell} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {list.map((incident) => {
            const distance = typeof incident.distance_km === "number" ? incident.distance_km.toFixed(1) : "--";
            return (
              <div key={incident.id} className="rounded-xl border border-slate-100 bg-white p-5 shadow-[0_2px_10px_-3px_rgba(15,23,42,0.05)]">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-slate-400">{formatDisplayId(incident.id)}</p>
                    <p className="text-lg font-bold text-slate-900">{incident.type}</p>
                  </div>
                  <SeverityBadge severity={incident.severity} />
                </div>

                <div className="mt-4 space-y-2 text-sm text-slate-700">
                  <div className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" /><LocationLabel locationValue={incident.locationRaw ?? incident.location} /></div>
                  <div className="flex items-center gap-2"><Activity size={14} className="text-slate-400" /><span className="font-semibold">{distance} km</span> away</div>
                  <div className="flex items-center gap-2 text-slate-500"><Clock size={14} /><span className="text-xs">{formatEventTime(incident.timestamp)}</span></div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {canUpdateIncidents ? (
                    <Button onClick={() => acknowledge(incident.id)} disabled={statusSubmittingId === incident.id || responseSubmitting}>
                      {statusSubmittingId === incident.id ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Check size={14} className="mr-1.5" />} Acknowledge
                    </Button>
                  ) : null}
                  {canDispatchIncidents ? (
                    <Button variant="outline" onClick={() => openServiceDialog(incident)} disabled={statusSubmittingId === incident.id || responseSubmitting}>
                      <Send size={14} className="mr-1.5" /> Log Response
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
          <SelectTrigger className="h-10 w-[110px]">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent>
            {[10, 20, 50].map((size) => (
              <SelectItem key={size} value={String(size)}>{size}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" disabled={loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
        <Button variant="outline" disabled={loading || page >= (meta.pages || 1)} onClick={() => setPage((p) => Math.min(meta.pages || 1, p + 1))}>Next</Button>
      </div>

      <Dialog open={serviceDialogOpen} onOpenChange={(open) => !responseSubmitting && setServiceDialogOpen(open)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Fire Service Response</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={serviceStatus} onValueChange={setServiceStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_RESPONSE_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note (Optional)</Label>
              <Textarea value={serviceNote} onChange={(event) => setServiceNote(event.target.value)} placeholder="e.g. Engine 4 dispatched from station 1" rows={4} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setServiceDialogOpen(false)} disabled={responseSubmitting}>Cancel</Button>
            {canDispatchIncidents ? (
              <Button onClick={submitServiceResponse} disabled={responseSubmitting} className="gap-2">
                {responseSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Log update
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

